import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button as AntButton, App, Spin, Table, Typography, Upload, message } from "antd";
import DashboardLayout from "~/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "~/examples/Navbars/DashboardNavbar";
import useImageCompression from "~/hooks/useImageCompression";
import {
  Autocomplete,
  Card,
  CardHeader,
  Grid,
  TableContainer,
  TextField as MuiTextField,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MDBox from "~/components/MDBox";
import SelectField from "~/components/Extras/SelectField";
import { autocompleteStyleOverrides } from "~/utils/utils";

import { Formik } from "formik";
import { debounce, formInitialValues, validationSchema } from "./helper";
import PlantData from "./PlantData";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import MDButton from "~/components/MDButton";
import { useNavigate, useParams } from "react-router-dom";
import {
  usefetchLocationReport,
  usefetchMonitoringReport,
  usefetchPlantReport,
  usefetchPlantationReport,
  userfetchPlottingReport,
} from "~/queries/reportQueries";
import LocationData from "./LocationData";
import PlotData from "./PlotData";
import PlantationData from "./PlantationData";
import { getLocations } from "~/services/location.service";
import { getPlottingData } from "~/services/plotting.service";
import { useMutation } from "@tanstack/react-query";
import { getPlants, getPlantsByPlotId } from "~/services/plant.service";
import MonitoringData from "./MonitoringData";

const ReportData = () => {
  // Material UI Hooks
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  // React-router hooks
  const navigate = useNavigate();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [dataToExport, setDataToExport] = useState([]);
  const [showselectField, setShowselectField] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [activeComponent, setActiveComponent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [plantNameQuery, setPlantNameQuery] = useState("");
  const [openPlotting, setOpenPlotting] = useState(false);
  const [openPlantType, setOpenPlantType] = useState(false);

  // Variables declarations
  const format = "DD-MM-YYYY";
  const formRef = useRef(null);
  // Custom hooks
  const { compressedFile, isCompressing } = useImageCompression(fileList[0]?.originFileObj, "Plant_Img", formRef);
  // query mutation hooks
  const fetchPlantReport = usefetchPlantReport();
  const fetchLocationReport = usefetchLocationReport();
  const fetchPlottingReport = userfetchPlottingReport();
  const fetchPlantationReport = usefetchPlantationReport();
  const fetchMonitoringReport = usefetchMonitoringReport();
  const plottingData = useMutation({
    mutationFn: (payload) => getPlottingData(payload),
  });

  const locationData = useMutation({
    mutationFn: (payload) => getLocations(payload),
  });

  const plantMutation = useMutation({
    mutationFn: (payload) => getPlantsByPlotId(payload),
  });

  const handleSubmit = async (values, helpers) => {
    try {
      const fetchMinDate = values.Min_Date ? moment(values.Min_Date, 'DD-MM-YYYY').format("YYYY-MM-DD") : "";
      const fetchMaxDate = values.Max_Date ? moment(values.Max_Date, 'DD-MM-YYYY').format("YYYY-MM-DD") : "";
      if (values.Report_For === "Plant master") {
        const data = await fetchPlantReport.mutateAsync({ minDate: fetchMinDate, maxDate: fetchMaxDate });
        if (data.statusCode === 200) {
          setReportData(data.data.rows);
          setActiveComponent("PlantMaster");
        }
      } else if (values.Report_For === "Location master") {
        const data = await fetchLocationReport.mutateAsync({ minDate: fetchMinDate, maxDate: fetchMaxDate });
        if (data.statusCode === 200) {
          setReportData(data.data.rows);
          setActiveComponent("LocationMaster");
        }
      } else if (values.Report_For === "Plotting master") {
        const data = await fetchPlottingReport.mutateAsync({
          minDate: fetchMinDate, 
          maxDate: fetchMaxDate,
          location: values.Loc_Auto_ID,
        });
        if (data.statusCode === 200) {
          setReportData(data.data.rows.map((record) => ({ ...record, ...record.loc_mstr})));
          setActiveComponent("PlottingMaster");
        }
      } else if (values.Report_For === "Plantation master") {
        const data = await fetchPlantationReport.mutateAsync({
          minDate: fetchMinDate, 
          maxDate: fetchMaxDate,
          location: values.Loc_Auto_ID,
          plot: values.Plot_Auto_ID,
          plant: values.Plant_Auto_ID,
        });
        if (data.statusCode === 200) {
          setReportData(data.data.rows.map((record) => ({ ...record, ...record.plot_mstr, ...record.plant_mstr })));
          setActiveComponent("PlantationMaster");
          
        }
      } else if (values.Report_For === "Plant monitoring") {
        const data = await fetchMonitoringReport.mutateAsync({
          minDate: fetchMinDate, 
          maxDate: fetchMaxDate,
          location: values.Loc_Auto_ID,
          plot: values.Plot_Auto_ID,
          plant: values.Plant_Auto_ID,
        });
        if (data.statusCode === 200) {
          // console.log(data)
          // const fetchData = data.data.map((record) => ({ ...record, ...record.plantation_barcode_dtls }));
          
          setReportData(data.data);
          setActiveComponent("PlantMonitoring");
          // console.log(
          //   "first",
          //   data.data.map((record) => ({ ...record.plantation_barcode_dtls }))
          // );
        }
      }
    } catch (error) {
      // console.log("error on submit", error);
      message.error(error?.response?.data?.message);
      // Resetting form if error
      // helpers.resetForm();
    }
  };
  // Function to conditionally set the value prop for the DatePicker
  const getDatePickerValue = (field) => {
    if (field === null || field === "") {
      return null; // Don't pass the value prop if value is null
    }
    // Parse the date and return it in the desired format
    return moment(field, format);
  };

  const generateDatePickerProps = (label, name, fieldValue) => ({
    sx: { width: "100%" },
    name: name,
    label: label,
    format: format,
    disableFuture: true,
    minDate: fieldValue ? moment(fieldValue, format) : "",
  });

  const resetState = (helpers) => {
    setSearchQuery("");
    // setOptions([]);
    // monitoringCategoryMutation.reset();
    // setDisableFieldsDuringUpdate(false);
    if (helpers) helpers.resetForm();

    if (params?.id) navigate(-1);
  };

  const handleSuggestionInput = (searchText) => {
    // if (searchText === '' || searchText.length < 4) return;
    setSearchQuery(searchText);
    locationData.mutate({ filter: searchText });
  };

  function handlePlottingSuggestion(searchText) {
    // if (searchText === '' || searchText.length < 4) return;
    plottingData.mutate({
      filter: searchText,
      id: formRef.current?.values.Loc_Auto_ID,
    });
  }

  function handlePlantSuggestion(searchText) {
    setPlantNameQuery(searchText);
    plantMutation.mutate({ filter: searchText });
  }

  const debounceHandlerForPlotting = useCallback(debounce(handlePlottingSuggestion, 500), []);

  const debounceHandlerForSuggestions = useCallback(debounce(handleSuggestionInput, 500), []);

  const debounceHandlerForPlantType = useCallback(debounce(handlePlantSuggestion, 500), []);

  function handleAutocompleteChange(value, fieldName, idFieldName) {
    if (!value?.label) {
      formRef.current.setFieldValue(fieldName.trim(), "");
      formRef.current.setFieldValue(idFieldName.trim(), "");
    } else {
      // do something else
      formRef.current.setFieldValue(fieldName.trim(), value?.label);
      formRef.current.setFieldValue(idFieldName.trim(), value?.value);
    }
  }

  const handleReports = (value) => {
    // console.log(formRef.current.resetForm(), "tgiiss")
    setActiveComponent("")
    formRef.current.setFieldValue("Max_Date", "")
    formRef.current.setFieldValue("Min_Date", "")
    formRef.current.setFieldValue("Loc_Autocomplete_Name", "")
    formRef.current.setFieldValue("Loc_Auto_ID", "")
    formRef.current.setFieldValue("Plot_Autocomplete_Name", "")
    formRef.current.setFieldValue("Plot_Auto_ID", "")
    formRef.current.setFieldValue("Plant_Autocomplete_Name", "");
    formRef.current.setFieldValue("Plant_Auto_ID", "");
    if (value.target.value === "Plant master") {
      setShowselectField("PlantMaster");
    } else if (value.target.value === "Location master") {
      setShowselectField("LocationMaster");
    } else if (value.target.value === "Plotting master") {
      setShowselectField("PlottingMaster");
    } else if (value.target.value === "Plantation master") {
      setShowselectField("PlantationMaster");
    } else if (value.target.value === "Plant monitoring") {
      setShowselectField("PlantMonitoring");
    } else {
      setShowselectField("");
    }
  };

  useEffect(() => {
    locationData.mutate({ filter: searchQuery });

  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Spin tip="Compressing Image..." spinning={isCompressing}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Card>
              <MDBox px={2}>
                <CardHeader title="Report" />
              </MDBox>
              <MDBox py={2} px={2}>
                <Formik
                  initialValues={formInitialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnChange
                  innerRef={formRef}
                >
                  {({
                    errors,
                    handleSubmit,
                    handleChange,
                    touched,
                    values,
                    setFieldValue,
                    handleReset,
                    handleBlur,
                  }) => (
                    <form noValidate onSubmit={handleSubmit}>
                      <Grid container rowSpacing={2} columnSpacing={2}>
                        <Grid item lg={3} md={3} sm={3} xs={6}>
                          <SelectField
                            // disabled={disableFieldsDuringUpdate}
                            name="Report_For"
                            label="Select Report For"
                            onChange={(e) => {
                              handleChange(e), handleReports(e);
                            }}
                            options={[
                              { value: "", label: "Select" },
                              { value: "Plant master", label: "Plant Master" },
                              { value: "Location master", label: "Location Master" },
                              { value: "Plotting master", label: "Plotting Master" },
                              { value: "Plantation master", label: "Plantation Master" },
                              { value: "Plant monitoring", label: "Plant Monitoring" },
                            ]}
                          />
                        </Grid>
                        {showselectField === "PlottingMaster" ? (
                          <>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <Autocomplete
                                sx={autocompleteStyleOverrides}
                                id="location_Autocomplete"
                                label="Select Location"
                                name="Loc_Autocomplete_Name"
                                open={open}
                                onOpen={() => {
                                  setOpen(true);
                                }}
                                onClose={() => {
                                  setOpen(false);
                                }}
                                value={values?.Loc_Autocomplete_Name ?? ""}
                                options={
                                  locationData?.data?.data?.map((item) => ({
                                    value: item.Loc_Auto_ID,
                                    label: item.Loc_Name,
                                  })) || []
                                }
                                onChange={(event, newValue) => {
                                  handleChange(event);
                                  handleAutocompleteChange(newValue, "Loc_Autocomplete_Name", "Loc_Auto_ID");
                                }}
                                onInputChange={(e, value) => debounceHandlerForSuggestions(value)}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                  <MuiTextField
                                    {...params}
                                    size="small"
                                    name="Loc_Autocomplete_Name"
                                    label="Select Location"
                                    error={Boolean(touched?.Loc_Autocomplete_Name && errors?.Loc_Autocomplete_Name)}
                                    helperText={touched?.Loc_Autocomplete_Name && errors?.Loc_Autocomplete_Name}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <React.Fragment>
                                          {locationData.isLoading ? <Spin size="small" /> : null}
                                          {params.InputProps.endAdornment}
                                        </React.Fragment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps("From Date", "Min_Date", moment("2023-12-01"))}
                                  // maxDate={moment().subtract(1, 'days')}
                                  onChange={(e) => setFieldValue("Min_Date", moment(e).format(format))}
                                  value={getDatePickerValue(values.Min_Date)}
                                  slotProps={{
                                    textField: {
                                      helperText: touched.Min_Date && errors.Min_Date,
                                      error: touched.Min_Date && errors.Min_Date,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps("To Date", "Max_Date", values.Min_Date ? values.Min_Date : moment("2023-12-01"))}
                                  onChange={(e) => setFieldValue("Max_Date", moment(e).format(format))}
                                  value={getDatePickerValue(values.Max_Date)}
                                  slotProps={{
                                    textField: {
                                      helperText: touched.Max_Date && errors.Max_Date,
                                      error: touched.Max_Date && errors.Max_Date,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </>
                        ) : showselectField === "PlantationMaster" || showselectField === "PlantMonitoring" ? (
                          <>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <Autocomplete
                                sx={autocompleteStyleOverrides}
                                id="location_Autocomplete"
                                label="Select Location"
                                name="Loc_Autocomplete_Name"
                                open={open}
                                onOpen={() => {
                                  setOpen(true);
                                }}
                                onClose={() => {
                                  setOpen(false);
                                }}
                                value={values?.Loc_Autocomplete_Name ?? ""}
                                options={
                                  locationData?.data?.data?.map((item) => ({
                                    value: item.Loc_Auto_ID,
                                    label: item.Loc_Name,
                                  })) || []
                                }
                                onChange={(event, newValue) => {
                                  handleChange(event);
                                  handleAutocompleteChange(newValue, "Loc_Autocomplete_Name", "Loc_Auto_ID");
                                  setFieldValue("Plot_Autocomplete_Name", "");
                                  setFieldValue("Plot_Auto_ID", "");
                                  plottingData.mutate({
                                    id: newValue?.value,
                                    filter: "",
                                  });
                                }}
                                onInputChange={(e, value) => debounceHandlerForSuggestions(value)}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                  <MuiTextField
                                    {...params}
                                    size="small"
                                    name="Loc_Autocomplete_Name"
                                    label="Select Location"
                                    error={Boolean(touched?.Loc_Autocomplete_Name && errors?.Loc_Autocomplete_Name)}
                                    helperText={touched?.Loc_Autocomplete_Name && errors?.Loc_Autocomplete_Name}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <React.Fragment>
                                          {locationData.isLoading ? <Spin size="small" /> : null}
                                          {params.InputProps.endAdornment}
                                        </React.Fragment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <Autocomplete
                                sx={autocompleteStyleOverrides}
                                id="plot_Autocomplete"
                                label="Select Plotting"
                                name="Plot_Autocomplete_Name"
                                open={openPlotting}
                                onOpen={() => {
                                  setOpenPlotting(true);
                                }}
                                onClose={() => {
                                  setOpenPlotting(false);
                                }}
                                value={values?.Plot_Autocomplete_Name ?? ""}
                                options={
                                  plottingData?.data?.data?.map((item) => ({
                                    value: item.Plot_Auto_ID,
                                    label: item.Plot_Name,
                                  })) || []
                                }
                                onChange={(event, newValue) => {
                                  handleChange(event);
                                  handleAutocompleteChange(newValue, "Plot_Autocomplete_Name", "Plot_Auto_ID");
                                  setFieldValue("Plant_Autocomplete_Name", "");
                                  setFieldValue("Plant_Auto_ID", "");
                                  plantMutation.mutate({
                                    id: newValue?.value,
                                    filter: "",
                                  });
                                }}
                                onInputChange={(event, value) => debounceHandlerForPlotting(value)}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                  <MuiTextField
                                    {...params}
                                    size="small"
                                    name="Plot_Autocomplete_Name"
                                    label="Select Plotting"
                                    error={Boolean(touched?.Plot_Autocomplete_Name && errors?.Plot_Autocomplete_Name)}
                                    helperText={touched?.Plot_Autocomplete_Name && errors?.Plot_Autocomplete_Name}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <React.Fragment>
                                          {plottingData.isLoading ? <Spin size="small" /> : null}
                                          {params.InputProps.endAdornment}
                                        </React.Fragment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item lg={3} md={4} sm={6} xs={12}>
                              {/* new changes - tag v3.0.3  */}
                              <Autocomplete
                                sx={autocompleteStyleOverrides}
                                id="Plant_Autocomplete_Name"
                                label="Select Plant Name"
                                name="Plant_Autocomplete_Name"
                                open={openPlantType}
                                onOpen={() => {
                                  setOpenPlantType(true);
                                }}
                                onClose={() => {
                                  setOpenPlantType(false);
                                }}
                                value={values?.Plant_Autocomplete_Name ?? ""}
                                options={
                                  plantMutation.data?.data?.map((item) => ({
                                    value: item.Plant_Auto_ID,
                                    label: item.Plant_Local_Name,
                                  })) || []
                                }
                                onChange={(event, newValue) => {
                                  handleChange(event);
                                  handleAutocompleteChange(newValue, "Plant_Autocomplete_Name", "Plant_Auto_ID");
                                }}
                                onInputChange={(e, value) => debounceHandlerForPlantType(value)}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                  <MuiTextField
                                    {...params}
                                    size="small"
                                    name="Plant_Autocomplete_Name"
                                    label="Select Plant Name"
                                    error={Boolean(touched?.Plant_Autocomplete_Name && errors?.Plant_Autocomplete_Name)}
                                    helperText={touched?.Plant_Autocomplete_Name && errors?.Plant_Autocomplete_Name}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <React.Fragment>
                                          {plantMutation.isLoading ? <Spin size="small" /> : null}
                                          {params.InputProps.endAdornment}
                                        </React.Fragment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps("From Date", "Min_Date", moment("2023-12-01"))}
                                  // maxDate={moment().subtract(1, 'days')}
                                  onChange={(e) => setFieldValue("Min_Date", moment(e).format(format))}
                                  value={getDatePickerValue(values.Min_Date)}
                                  error={Boolean(touched?.Min_Date && errors?.Min_Date)}
                                  helperText={touched?.Min_Date && errors?.Min_Date}
                                  slotProps={{
                                    textField: {
                                      helperText: touched.Min_Date && errors.Min_Date,
                                      error: touched.Min_Date && errors.Min_Date,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} md={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps("To Date", "Max_Date", values.Min_Date ? values.Min_Date : moment("2023-12-01"))}
                                  onChange={(e) => setFieldValue("Max_Date", moment(e).format(format))}
                                  value={getDatePickerValue(values.Max_Date)}
                                  error={Boolean(touched?.Max_Date && errors?.Max_Date)}
                                  helperText={touched?.Max_Date && errors?.Max_Date}
                                  slotProps={{
                                    textField: {
                                      helperText: touched.Max_Date && errors.Max_Date,
                                      error: touched.Max_Date && errors.Max_Date,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </>
                        ) : showselectField === "PlantMaster" || showselectField === "LocationMaster" ? (
                          <>
                           <Grid item lg={3} md={3} sm={3} xs={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                {...generateDatePickerProps("From Date", "Min_Date", moment("2023-12-01"))}
                                // maxDate={moment().subtract(1, 'days')}
                                onChange={(e) => setFieldValue("Min_Date", moment(e).format(format))}
                                value={getDatePickerValue(values.Min_Date)}
                                error={Boolean(touched?.Min_Date && errors?.Min_Date)}
                                helperText={touched?.Min_Date && errors?.Min_Date}
                                slotProps={{
                                  textField: {
                                    helperText: touched.Min_Date && errors.Min_Date,
                                    error: touched.Min_Date && errors.Min_Date,
                                  },
                                }}
                                renderInput={(params) => (
                                  <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                )}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                {...generateDatePickerProps("To Date", "Max_Date", values.Min_Date ? values.Min_Date : moment("2023-12-01"))}
                                onChange={(e) => setFieldValue("Max_Date", moment(e).format(format))}
                                value={getDatePickerValue(values.Max_Date)}
                                error={Boolean(touched?.Max_Date && errors?.Max_Date)}
                                helperText={touched?.Max_Date && errors?.Max_Date}
                                slotProps={{
                                  textField: {
                                    helperText: touched.Max_Date && errors.Max_Date,
                                    error: touched.Max_Date && errors.Max_Date,
                                  },
                                }}
                                renderInput={(params) => (
                                  <MuiTextField {...params} fullWidth onKeyDown={(e) => e.preventDefault()} />
                                )}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </>
                        ) : ""}
                       
                        <Grid container my={2} spacing={2} justifyContent="end" alignItems="center">
                          <Stack direction="row" spacing={2} useFlexGap>
                            <MDButton
                              sx={{ px: 5 }}
                              variant="contained"
                              color="error"
                              size={matchesXs ? "small" : "large"}
                              type="reset"
                              onClick={(e) => {
                                handleReset(e);
                                resetState(null);
                              }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              sx={{ px: 6 }}
                              type="submit"
                              fullWidth
                              variant="contained"
                              color="success"
                              size={matchesXs ? "small" : "large"}
                              // disabled={createPlantSurveyMutation.isLoading}
                              onClick={() => {
                                console.log("ON SUBMIT", formRef.current);
                                Boolean(errors?.conditionalValidation) && message.error(errors.conditionalValidation);
                              }}
                            >
                              Submit
                            </MDButton>
                          </Stack>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </MDBox>
              <MDBox pt={1}>
                {activeComponent === "PlantMaster" ? (
                  <PlantData reportData={reportData} />
                ) : activeComponent === "LocationMaster" ? (
                  <LocationData reportData={reportData} />
                ) : activeComponent === "PlottingMaster" ? (
                  <PlotData reportData={reportData} />
                ) : activeComponent === "PlantationMaster" ? (
                  <PlantationData reportData={reportData} />
                ) : activeComponent === "PlantMonitoring" ? (
                  <MonitoringData reportData={reportData} />
                ) : (
                  ""
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Spin>
    </DashboardLayout>
  );
};

export default ReportData;
